import React from 'react'
import styled from 'styled-components'

import { convertWideKanaToHalfKana } from 'utils/commonUtils'
import BaseGroup from './BaseGroup'
import { FormTitleProps } from '../items/FormTitle'
import TextAreaLine from '../items/TextAreaLine'
import { useUpdateLog } from 'utils/log'
import { FormRefarensImageProps } from '../items/FormRefarensImage'
import ErrorMessage from '../items/ErrorMessage'

/**
 * MultiTextBoxGroupのタイプ定義
 */
export type MultiTextBoxProps = FormTitleProps &
  FormRefarensImageProps & {
    fields: Array<{
      /** フィールドのkey */
      fieldKey: string
      /** 入力テキスト */
      text: string
      /** 入力欄横幅 */
      width: string
      maxLength?: {
        /** 文字数 */
        length: number
        /** 文字数チェックを半角の文字数で確認するフラグ */
        checkHalfStr?: boolean
      }
      isInvalid?: boolean
      /** 入力文字変更時のコールバック */
      onChanged: (value: string) => void
      /** プレースホルダ― */
      placeholder?: string
    }>
    validationMessage?: string
  }

/** 複数入力欄グループ */

const MultiTextBoxGroup: React.FC<MultiTextBoxProps> = (props) => {
  const {
    title,
    isRequired,
    isOption,
    description,
    hideTitle,
    fields,
    image,
    validationMessage,
  } = props

  const [inputText, setInputText] = React.useState<{
    [fieldKey: string]: string
  }>(
    fields
      ? fields.reduce(
          (acc, field) => ({ ...acc, [field.fieldKey]: field.text }),
          {}
        )
      : ''
  )
  // バリデーション結果を格納（バリデーションエラーなら値にtrueを保持）
  const [validateResults, setValidateResults] = React.useState<{
    [key: string]: boolean
  }>(
    fields
      ? fields.reduce(
          (acc, field) => ({ ...acc, [field.fieldKey]: field.isInvalid }),
          {}
        )
      : ''
  )

  const handleSetShowAlert = (isInvalid: boolean, key: string): void => {
    setValidateResults((prev) => ({ ...prev, [key]: isInvalid }))
  }

  //  入力値変更時のコールバック
  const handelTextChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>, key: string): void => {
      const newText = event.target.value
      const targetField = fields?.find((field) => field.fieldKey === key)

      if (
        targetField?.maxLength &&
        (targetField.maxLength.checkHalfStr
          ? convertWideKanaToHalfKana(newText).length
          : newText.length) > (targetField.maxLength.length ?? -1)
      ) {
        return
      }

      setInputText((prev) => ({ ...prev, [key]: newText }))

      if (targetField && targetField.onChanged) {
        targetField?.onChanged(newText)
      }
    },
    [fields]
  )

  // ハイライト中のものがあれば、アラートを出す。
  const showAlert = React.useMemo(
    () => Object.values(validateResults).some((el) => el),
    [validateResults]
  )

  useUpdateLog(
    title,
    {
      title: title,
      isRequired: isRequired,
    },
    [props]
  )

  return (
    <>
      <BaseGroup
        title={title}
        isRequired={isRequired}
        isOption={isOption}
        description={description}
        hideTitle={hideTitle}
        hasField={Boolean(fields)}
        image={image}
      >
        <Flex>
          {fields?.map((field) => (
            <TextAreaLine
              key={field.fieldKey}
              width={field.width}
              inputText={inputText[field.fieldKey]}
              placeholder={field.placeholder}
              onChange={(event): void => {
                handelTextChange(event, field.fieldKey)
              }}
              isInvalid={field.isInvalid}
              setShowAlert={(isInvalid: boolean): void =>
                handleSetShowAlert(isInvalid, field.fieldKey)
              }
            />
          ))}
        </Flex>
      </BaseGroup>
      {showAlert && validationMessage && (
        <ErrorMessage message={validationMessage} justifyContent="left" />
      )}
    </>
  )
}

export const Flex = styled.div`
  display: flex;
  justify-content: space-between;
`

export default MultiTextBoxGroup
